/**
 * Card spotlight (founder 2026-07-13): the "See it live →" links on Agency ›
 * Cards open a real dashboard page with `?spotlight=<renderId>` (a `#card-<id>`
 * hash also works). This hook — mounted ONCE by each layout (the normal app AND
 * the read-only demo) — finds the card that emitted `data-card-anchor="<id>"`,
 * scrolls it into view, and flashes an accent ring a few times so the founder's
 * eye lands on the exact card he clicked.
 *
 * Two robustness details:
 *  - Cards load their data async, so the anchor may not exist on first paint —
 *    we retry for a few seconds until it appears.
 *  - The Hideable wrapper renders `display:contents` when idle (no layout box of
 *    its own), so we resolve the anchor to the first child that actually has a
 *    box before scrolling/flashing (resolveBox).
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/** Escape a value for use inside an attribute selector. renderIds are plain
 *  slugs today, but the value comes from the URL, so never trust it raw. */
function cssEscape(s: string): string {
  if (typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(s);
  }
  return s.replace(/["\\]/g, "\\$&");
}

/** The anchor element, or its first laid-out child when the anchor itself has no
 *  box (Hideable's idle `display:contents` wrapper). */
function resolveBox(anchor: Element | null): HTMLElement | null {
  if (!(anchor instanceof HTMLElement)) return null;
  const r = anchor.getBoundingClientRect();
  if (r.width > 0 || r.height > 0) return anchor;
  for (const child of Array.from(anchor.children)) {
    if (child instanceof HTMLElement) {
      const cr = child.getBoundingClientRect();
      if (cr.width > 0 || cr.height > 0) return child;
    }
  }
  return anchor;
}

function readTarget(search: string, hash: string): string | null {
  const fromQuery = new URLSearchParams(search).get("spotlight");
  if (fromQuery) return fromQuery;
  if (hash.startsWith("#card-")) return hash.slice("#card-".length);
  return null;
}

export function useCardSpotlight(): void {
  const location = useLocation();
  useEffect(() => {
    const raw = readTarget(location.search, location.hash);
    if (!raw) return undefined;
    let id: string;
    try {
      id = decodeURIComponent(raw);
    } catch {
      id = raw;
    }

    let cancelled = false;
    let flashed: HTMLElement | null = null;
    let removeTimer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const tryFlash = (): void => {
      if (cancelled) return;
      const anchor = document.querySelector(`[data-card-anchor="${cssEscape(id)}"]`);
      const box = resolveBox(anchor);
      if (box) {
        box.classList.add("dd-spotlight-target");
        try {
          box.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch {
          box.scrollIntoView();
        }
        // Restart the CSS animation cleanly if this fires again for the same box.
        box.classList.remove("dd-spotlight-flash");
        void box.offsetWidth; // force reflow so re-adding the class replays it
        box.classList.add("dd-spotlight-flash");
        flashed = box;
        removeTimer = setTimeout(() => box.classList.remove("dd-spotlight-flash"), 3200);
        return;
      }
      // Cards still loading their data → the anchor isn't in the DOM yet. Retry
      // briefly, then give up (the page still opened on the right card).
      if (Date.now() - startedAt < 6000) {
        removeTimer = setTimeout(tryFlash, 250);
      }
    };

    // Let the first paint settle before hunting for the anchor.
    const kickoff = setTimeout(tryFlash, 120);

    return () => {
      cancelled = true;
      clearTimeout(kickoff);
      if (removeTimer) clearTimeout(removeTimer);
      if (flashed) flashed.classList.remove("dd-spotlight-flash");
    };
  }, [location.pathname, location.search, location.hash]);
}
